import {
  EventStreamContentType,
  fetchEventSource,
  type EventSourceMessage,
} from "@microsoft/fetch-event-source";
import type { OperationGrant, RuntimeSessionResponse } from "@situate/contracts/platform";
import { onDeploymentChanged } from "@/features/deployment-selection/model/deployment-events";
import { ApiError } from "@/shared/api/api-error";
import { env } from "@/shared/config/env";
import { createHttpClient, normalizeApiBaseUrl } from "@/shared/api/http-client";
import type { BinaryHttpResponse, HttpClient, HttpRequestOptions } from "@/shared/api/http-client";

export interface RuntimeStreamHandlers {
  /** Invoked for every parsed SSE message received on the stream. */
  onMessage: (message: EventSourceMessage) => void;
}

/** Identifies authorization failures while stopping fetchEventSource's retry loop. */
class FatalRuntimeStreamError extends Error {}

const REFRESH_AT_TTL_FRACTION = 0.8;

interface RuntimeSessionRefreshOptions {
  refreshAt: number;
  refreshSession: () => Promise<RuntimeSessionResponse>;
}

function normalizePath(path: string): string {
  return path.startsWith("/") ? path : `/${path}`;
}

/**
 * HTTP client scoped to a deployment's runtime API, authenticated with a
 * short-lived runtime session access token (see `fetchRuntimeSession`).
 *
 * Provides standard JSON request methods plus `subscribe` for authenticated
 * Server-Sent Events streams, since the native `EventSource` API cannot send
 * an `Authorization` header.
 */
export class RuntimeHttpClient {
  readonly deploymentId: string;
  private baseUrl: string;
  private accessToken: string;
  private jsonClient: HttpClient;
  private refreshAt = Number.POSITIVE_INFINITY;
  private refreshSession: (() => Promise<RuntimeSessionResponse>) | null = null;
  private refreshPromise: Promise<void> | null = null;

  constructor(
    deploymentId: string,
    baseUrl: string,
    accessToken: string,
    refreshOptions?: RuntimeSessionRefreshOptions
  ) {
    this.deploymentId = deploymentId;
    this.baseUrl = normalizeApiBaseUrl(baseUrl, {
      allowHttp: env.VITE_DEPLOY_ENV === "local",
    });
    this.accessToken = accessToken;
    this.jsonClient = this.createJsonClient();
    if (refreshOptions) {
      this.refreshAt = refreshOptions.refreshAt;
      this.refreshSession = refreshOptions.refreshSession;
    }
  }

  get<T>(path: string, options?: HttpRequestOptions): Promise<T> {
    return this.request(() => this.jsonClient.get<T>(path, options));
  }

  post<T>(path: string, body?: unknown, options?: HttpRequestOptions): Promise<T> {
    return this.request(() => this.jsonClient.post<T>(path, body, options));
  }

  postBinary(path: string, body?: unknown, options?: HttpRequestOptions): Promise<ArrayBuffer> {
    return this.request(() => this.jsonClient.postBinary(path, body, options));
  }

  getBinary(path: string, options?: HttpRequestOptions): Promise<BinaryHttpResponse> {
    return this.request(() => this.jsonClient.getBinary(path, options));
  }

  put<T>(path: string, body?: unknown, options?: HttpRequestOptions): Promise<T> {
    return this.request(() => this.jsonClient.put<T>(path, body, options));
  }

  delete<T>(path: string, options?: HttpRequestOptions): Promise<T> {
    return this.request(() => this.jsonClient.delete<T>(path, options));
  }

  private createJsonClient(): HttpClient {
    return createHttpClient({
      baseUrl: this.baseUrl,
      getToken: async () => {
        await this.refreshAccessTokenIfNeeded();
        return this.accessToken;
      },
    });
  }

  private async request<T>(request: () => Promise<T>): Promise<T> {
    try {
      return await request();
    } catch (error) {
      if (
        !(error instanceof ApiError) ||
        error.statusCode !== 401 ||
        this.refreshSession === null
      ) {
        throw error;
      }

      await this.refreshAccessTokenIfNeeded(true);
      return request();
    }
  }

  private async refreshAccessTokenIfNeeded(force = false): Promise<void> {
    if (!force && Date.now() < this.refreshAt) return;
    if (this.refreshSession === null) return;
    if (this.refreshPromise !== null) return this.refreshPromise;

    this.refreshPromise = this.refreshSession()
      .then((runtimeSession) => this.updateSession(runtimeSession))
      .finally(() => {
        this.refreshPromise = null;
      });
    return this.refreshPromise;
  }

  updateSession(
    runtimeSession: RuntimeSessionResponse,
    refreshSession?: () => Promise<RuntimeSessionResponse>
  ): void {
    if (runtimeSession.deployment_id !== this.deploymentId) {
      throw new Error("Refreshed runtime session does not match the selected deployment");
    }

    this.baseUrl = normalizeApiBaseUrl(runtimeSession.runtime_api_base_url, {
      allowHttp: env.VITE_DEPLOY_ENV === "local",
    });
    this.accessToken = runtimeSession.access_token;
    this.refreshAt =
      runtimeSession.issued_at * 1_000 +
      runtimeSession.expires_in * 1_000 * REFRESH_AT_TTL_FRACTION;
    if (refreshSession !== undefined) this.refreshSession = refreshSession;
    this.jsonClient = this.createJsonClient();
  }

  /**
   * Subscribe to an authenticated Server-Sent Events stream on the runtime API.
   *
   * Resolves when `signal` is aborted and rejects when the connection fails or
   * closes unexpectedly. The caller owns retry timing so only one connection
   * lifecycle can be active at a time.
   */
  async subscribe(
    path: string,
    handlers: RuntimeStreamHandlers,
    signal: AbortSignal
  ): Promise<void> {
    await this.refreshAccessTokenIfNeeded();
    const url = `${this.baseUrl}${normalizePath(path)}`;

    await fetchEventSource(url, {
      headers: {
        Authorization: `Bearer ${this.accessToken}`,
        Accept: EventStreamContentType,
      },
      signal,
      // Stream hooks own page-visibility lifecycle; prevent fetchEventSource
      // from independently closing and recreating the same request.
      openWhenHidden: true,
      async onopen(response) {
        const contentType = response.headers.get("content-type");
        if (response.ok && contentType?.startsWith(EventStreamContentType)) {
          return;
        }

        if (response.status === 401 || response.status === 403) {
          throw new FatalRuntimeStreamError(
            `Runtime stream authorization failed with status ${response.status}`
          );
        }

        throw new Error(`Runtime stream failed to open with status ${response.status}`);
      },
      onmessage(message) {
        handlers.onMessage(message);
      },
      onclose() {
        if (!signal.aborted) {
          // Throwing routes an unexpected EOF through onerror, which keeps the
          // library's retry loop alive with its current backoff interval.
          throw new Error("Runtime stream closed unexpectedly");
        }
      },
      onerror(error) {
        // Always rethrow so fetchEventSource cannot start an independent retry
        // loop. Stream hooks own retry timing and abort the previous generation.
        throw error;
      },
    });
  }
}

export interface RuntimeHttpClientSnapshot {
  deploymentId: string | null;
  client: RuntimeHttpClient | null;
  operationGrants: readonly OperationGrant[];
  refreshSession: (() => Promise<void>) | null;
}

const EMPTY_RUNTIME_HTTP_CLIENT_SNAPSHOT: RuntimeHttpClientSnapshot = {
  deploymentId: null,
  client: null,
  operationGrants: [],
  refreshSession: null,
};

let runtimeHttpClientSnapshot = EMPTY_RUNTIME_HTTP_CLIENT_SNAPSHOT;
const runtimeHttpClientListeners = new Set<() => void>();

/** Synchronizes the application-wide Runtime API client with a newly issued session. */
export function setRuntimeSession(
  runtimeSession: RuntimeSessionResponse,
  refreshSession: () => Promise<RuntimeSessionResponse>
): void {
  const currentClient = runtimeHttpClientSnapshot.client;
  const client =
    currentClient !== null && currentClient.deploymentId === runtimeSession.deployment_id
      ? currentClient
      : new RuntimeHttpClient(
          runtimeSession.deployment_id,
          runtimeSession.runtime_api_base_url,
          runtimeSession.access_token,
          {
            refreshAt:
              runtimeSession.issued_at * 1_000 +
              runtimeSession.expires_in * 1_000 * REFRESH_AT_TTL_FRACTION,
            refreshSession,
          }
        );

  if (client === currentClient) client.updateSession(runtimeSession, refreshSession);

  runtimeHttpClientSnapshot = {
    deploymentId: runtimeSession.deployment_id,
    client,
    operationGrants: runtimeSession.operation_grants,
    refreshSession: async () => {
      await refreshSession();
    },
  };
  runtimeHttpClientListeners.forEach((listener) => listener());
}

/**
 * Clears the application-wide Runtime API client. When an expected deployment
 * is supplied, a newer deployment's client cannot be cleared accidentally.
 */
export function clearRuntimeSession(expectedDeploymentId?: string): void {
  if (
    expectedDeploymentId !== undefined &&
    runtimeHttpClientSnapshot.deploymentId !== expectedDeploymentId
  ) {
    return;
  }

  if (runtimeHttpClientSnapshot.client === null) return;

  runtimeHttpClientSnapshot = EMPTY_RUNTIME_HTTP_CLIENT_SNAPSHOT;
  runtimeHttpClientListeners.forEach((listener) => listener());
}

export function getRuntimeHttpClientSnapshot(): RuntimeHttpClientSnapshot {
  return runtimeHttpClientSnapshot;
}

export function subscribeToRuntimeHttpClient(listener: () => void): () => void {
  runtimeHttpClientListeners.add(listener);
  return () => runtimeHttpClientListeners.delete(listener);
}

// A deployment change invalidates the previous deployment's URL, token, and
// grants before any feature can accidentally reuse them.
onDeploymentChanged(({ deploymentId }) => {
  if (
    runtimeHttpClientSnapshot.deploymentId !== null &&
    runtimeHttpClientSnapshot.deploymentId !== deploymentId
  ) {
    clearRuntimeSession();
  }
});
